import React, { useMemo, Children, Component } from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon, CloudSun, Play, BookOpen, Sparkles, Calendar, ArrowRight } from 'lucide-react';
import { ModuleData, ModuleId, AppState } from '../../types';
import { GroupId } from '../../config/systemConfig';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useAppStore } from '../../hooks/useAppStore';
import { getModuleDisplayInfo } from '../../config/moduleContent';
import { APP_NAME } from '../../config/constants';
import * as Icons from 'lucide-react';
interface HomePageProps {
  modules: ModuleData[];
  state: AppState;
  selectedGroup: GroupId;
  displayName: string;
  onStartPractice: (moduleId: ModuleId) => void;
  onNavigateToModules: () => void;
  onModuleSelect: (module: ModuleData) => void;
}
// Dutch day names
const DAYS = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];
export function HomePage({
  modules,
  state,
  selectedGroup,
  displayName,
  onStartPractice,
  onNavigateToModules,
  onModuleSelect
}: HomePageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions
  } = useAppStore();
  // Time-based greeting
  const hour = new Date().getHours();
  let greeting = 'Hallo';
  let GreetingIcon = CloudSun;
  if (hour < 12) {
    greeting = 'Goedemorgen';
    GreetingIcon = Sun;
  } else if (hour < 18) {
    greeting = 'Goedemiddag';
    GreetingIcon = CloudSun;
  } else {
    greeting = 'Goedenavond';
    GreetingIcon = Moon;
  }
  // Calculate days practiced this week
  const weekActivity = useMemo(() => {
    const now = new Date();
    const currentDay = now.getDay();
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    const monday = new Date(now);
    monday.setDate(now.getDate() + mondayOffset);
    monday.setHours(0, 0, 0, 0);
    const days: boolean[] = Array(7).fill(false);
    if (state.questionStates) {
      Object.values(state.questionStates).forEach(qState => {
        if (qState.history && qState.history.length > 0) {
          qState.history.forEach(attempt => {
            const attemptDate = new Date(attempt.timestamp);
            attemptDate.setHours(0, 0, 0, 0);
            const dayDiff = Math.floor((attemptDate.getTime() - monday.getTime()) / (1000 * 60 * 60 * 24));
            if (dayDiff >= 0 && dayDiff < 7) {
              days[dayDiff] = true;
            }
          });
        }
      });
    }
    return days;
  }, [state.questionStates]);
  const activeDays = weekActivity.filter(Boolean).length;
  // Find the best module to continue with
  const suggestedModule = useMemo(() => {
    // First, find modules that have been started but not mastered
    const inProgress = modules.filter(m => {
      const mastery = getModuleMasteryForGroup(m.id);
      const answered = getAnsweredQuestions(m.id).length;
      return (answered > 0 || mastery > 0) && mastery < 95;
    });
    if (inProgress.length > 0) {
      // Return the one with lowest mastery (needs most work)
      return inProgress.reduce((prev, curr) => getModuleMasteryForGroup(curr.id) < getModuleMasteryForGroup(prev.id) ? curr : prev);
    }
    // If no in-progress modules, return first module
    return modules[0];
  }, [modules, getModuleMasteryForGroup, getAnsweredQuestions]);
  // Check if user has started anything
  const hasStarted = modules.some(m => {
    const answered = getAnsweredQuestions(m.id).length;
    return answered > 0;
  });
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: 'easeOut'
      }
    }
  };
  return <motion.div className="space-y-8 pb-12" variants={containerVariants} initial="hidden" animate="visible" data-id="element-3558">
      {/* Greeting Section */}
      <motion.div variants={itemVariants} data-id="element-3559">
        <div className="flex items-center gap-3 mb-2" data-id="element-3560">
          <GreetingIcon className="w-8 h-8 text-amber-400 fill-amber-400" data-id="element-3561" />
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight" data-id="element-3562">
            {greeting}, {displayName}!
          </h1>
        </div>
        <p className="text-slate-500 text-lg font-medium" data-id="element-3563">
          Groep {selectedGroup} •{' '}
          {new Date().toLocaleDateString('nl-NL', {
          weekday: 'long',
          day: 'numeric',
          month: 'long'
        })}
        </p>
      </motion.div>

      {/* Week Progress - Simple, encouraging */}
      <motion.div variants={itemVariants} data-id="element-3564">
        <Card className="p-5 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-100" data-id="element-3565">
          <div className="flex items-center justify-between" data-id="element-3566">
            <div className="flex items-center gap-3" data-id="element-3567">
              <div className="p-2.5 bg-white rounded-xl shadow-sm" data-id="element-3568">
                <Calendar className="w-5 h-5 text-indigo-600" data-id="element-3569" />
              </div>
              <div data-id="element-3570">
                <p className="font-bold text-slate-900" data-id="element-3571">Deze week</p>
                <p className="text-sm text-slate-600" data-id="element-3572">
                  {activeDays === 0 && 'Begin vandaag met oefenen!'}
                  {activeDays === 1 && 'Je hebt 1 dag geoefend'}
                  {activeDays > 1 && activeDays < 5 && `Je hebt ${activeDays} dagen geoefend`}
                  {activeDays >= 5 && `Geweldig! ${activeDays} dagen geoefend 🎉`}
                </p>
              </div>
            </div>
            <div className="flex gap-1.5" data-id="element-3573">
              {weekActivity.map((active, i) => <div key={i} className={`w-3 h-3 rounded-full transition-all ${active ? 'bg-emerald-500' : 'bg-slate-200'}`} title={DAYS[(i + 1) % 7]} data-id="element-3574" />)}
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Main Action Card */}
      <motion.div variants={itemVariants} data-id="element-3575">
        {hasStarted && suggestedModule ? <SuggestedPracticeCard module={suggestedModule} selectedGroup={selectedGroup} onStart={() => onStartPractice(suggestedModule.id)} getModuleMasteryForGroup={getModuleMasteryForGroup} data-id="element-3576" /> : <WelcomeCard onStart={onNavigateToModules} data-id="element-3577" />}
      </motion.div>

      {/* Subject Grid - Workbook Style */}
      <motion.div variants={itemVariants} data-id="element-3578">
        <div className="flex items-center justify-between mb-4" data-id="element-3579">
          <h2 className="text-xl font-bold text-slate-900" data-id="element-3580">Jouw vakken</h2>
          <button onClick={onNavigateToModules} className="text-sm font-semibold text-indigo-600 hover:text-indigo-700 flex items-center gap-1" data-id="element-3581">
            Alle vakken <ArrowRight className="w-4 h-4" data-id="element-3582" />
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-3583">
          {modules.slice(0, 8).map((module, index) => <WorkbookCard key={module.id} module={module} selectedGroup={selectedGroup} index={index} onClick={() => onModuleSelect(module)} onStart={() => onStartPractice(module.id)} getModuleMasteryForGroup={getModuleMasteryForGroup} getAnsweredQuestions={getAnsweredQuestions} data-id="element-3584" />)}
        </div>
      </motion.div>
    </motion.div>;
}
// Suggested Practice Card - What to do today
function SuggestedPracticeCard({
  module,
  selectedGroup,
  onStart,
  getModuleMasteryForGroup
}: {
  module: ModuleData;
  selectedGroup: GroupId;
  onStart: () => void;
  getModuleMasteryForGroup: (moduleId: string) => number;
}) {
  const IconComponent = (Icons as any)[module.icon] || Icons.BookOpen;
  const displayInfo = getModuleDisplayInfo(module.id, selectedGroup);
  const title = displayInfo?.title || module.title;
  const mastery = getModuleMasteryForGroup(module.id);
  // Encouraging message based on progress
  let message = 'Ga verder waar je gebleven was';
  if (mastery < 20) message = 'Je bent goed begonnen, ga zo door!';else if (mastery < 50) message = 'Je bent lekker op weg!';else if (mastery < 80) message = 'Nog even doorzetten, je bent er bijna!';else message = 'Bijna klaar met dit vak!';
  const colorMap: Record<string, {
    bg: string;
    icon: string;
    border: string;
  }> = {
    'text-blue-500': {
      bg: 'bg-blue-500',
      icon: 'text-white',
      border: 'border-blue-400'
    },
    'text-indigo-500': {
      bg: 'bg-indigo-500',
      icon: 'text-white',
      border: 'border-indigo-400'
    },
    'text-emerald-500': {
      bg: 'bg-emerald-500',
      icon: 'text-white',
      border: 'border-emerald-400'
    },
    'text-rose-500': {
      bg: 'bg-rose-500',
      icon: 'text-white',
      border: 'border-rose-400'
    },
    'text-amber-500': {
      bg: 'bg-amber-500',
      icon: 'text-white',
      border: 'border-amber-400'
    },
    'text-purple-500': {
      bg: 'bg-purple-500',
      icon: 'text-white',
      border: 'border-purple-400'
    },
    'text-teal-500': {
      bg: 'bg-teal-500',
      icon: 'text-white',
      border: 'border-teal-400'
    },
    'text-cyan-500': {
      bg: 'bg-cyan-500',
      icon: 'text-white',
      border: 'border-cyan-400'
    }
  };
  const colors = colorMap[module.color] || {
    bg: 'bg-indigo-500',
    icon: 'text-white',
    border: 'border-indigo-400'
  };
  return <Card className="overflow-hidden" data-id="element-3585">
      <div className="flex flex-col sm:flex-row" data-id="element-3586">
        <div className={`${colors.bg} p-6 sm:p-8 flex items-center justify-center sm:w-1/3`} data-id="element-3587">
          <div className="text-center" data-id="element-3588">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3" data-id="element-3589">
              <IconComponent className={`w-8 h-8 ${colors.icon}`} strokeWidth={2.5} data-id="element-3590" />
            </div>
            <h3 className="text-white font-bold text-lg" data-id="element-3591">{title}</h3>
          </div>
        </div>
        <div className="p-6 sm:p-8 flex-1 flex flex-col justify-center" data-id="element-3592">
          <div className="flex items-center gap-2 mb-2" data-id="element-3593">
            <Sparkles className="w-5 h-5 text-amber-500 fill-amber-500" data-id="element-3594" />
            <span className="text-sm font-bold text-amber-600 uppercase tracking-wide" data-id="element-3595">
              Vandaag oefenen
            </span>
          </div>
          <p className="text-slate-600 mb-6 text-lg" data-id="element-3596">{message}</p>
          <Button onClick={onStart} variant="primary" size="lg" className="w-full sm:w-auto" data-id="element-3597">
            <Play className="w-5 h-5 mr-2 fill-current" data-id="element-3598" />
            Ga oefenen
          </Button>
        </div>
      </div>
    </Card>;
}
// Welcome Card for new users
function WelcomeCard({
  onStart
}: {
  onStart: () => void;
}) {
  return <Card className="p-8 sm:p-10 text-center bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-100" data-id="element-3599">
      <motion.div className="w-20 h-20 bg-white rounded-full shadow-lg flex items-center justify-center mx-auto mb-6 text-5xl" animate={{
      rotate: [0, 10, -10, 0]
    }} transition={{
      repeat: Infinity,
      duration: 4,
      repeatDelay: 2
    }} data-id="element-3600">
        👋
      </motion.div>
      <h2 className="text-2xl sm:text-3xl font-black text-slate-900 mb-3" data-id="element-3601">
        Welkom bij {APP_NAME}!
      </h2>
      <p className="text-slate-600 text-lg mb-8 max-w-md mx-auto" data-id="element-3602">
        Kies een vak om te beginnen met oefenen. Je kunt altijd wisselen tussen
        vakken.
      </p>
      <Button onClick={onStart} variant="primary" size="xl" data-id="element-3603">
        <BookOpen className="w-5 h-5 mr-2" data-id="element-3604" />
        Kies je eerste vak
      </Button>
    </Card>;
}
// Workbook-style subject card (minimal stats)
function WorkbookCard({
  module,
  selectedGroup,
  index,
  onClick,
  onStart,
  getModuleMasteryForGroup,
  getAnsweredQuestions
}: {
  module: ModuleData;
  selectedGroup: GroupId;
  index: number;
  onClick: () => void;
  onStart: () => void;
  getModuleMasteryForGroup: (moduleId: string) => number;
  getAnsweredQuestions: (moduleId: string) => any[];
}) {
  const IconComponent = (Icons as any)[module.icon] || Icons.BookOpen;
  const displayInfo = getModuleDisplayInfo(module.id, selectedGroup);
  const title = displayInfo?.title || module.title;
  const mastery = getModuleMasteryForGroup(module.id);
  const answered = getAnsweredQuestions(module.id).length;
  const isStarted = answered > 0 || mastery > 0;
  const isMastered = mastery >= 95;
  const colorMap: Record<string, {
    bg: string;
    icon: string;
    border: string;
    light: string;
  }> = {
    'text-blue-500': {
      bg: 'bg-blue-500',
      icon: 'text-blue-600',
      border: 'border-blue-200',
      light: 'bg-blue-50'
    },
    'text-indigo-500': {
      bg: 'bg-indigo-500',
      icon: 'text-indigo-600',
      border: 'border-indigo-200',
      light: 'bg-indigo-50'
    },
    'text-emerald-500': {
      bg: 'bg-emerald-500',
      icon: 'text-emerald-600',
      border: 'border-emerald-200',
      light: 'bg-emerald-50'
    },
    'text-rose-500': {
      bg: 'bg-rose-500',
      icon: 'text-rose-600',
      border: 'border-rose-200',
      light: 'bg-rose-50'
    },
    'text-amber-500': {
      bg: 'bg-amber-500',
      icon: 'text-amber-600',
      border: 'border-amber-200',
      light: 'bg-amber-50'
    },
    'text-purple-500': {
      bg: 'bg-purple-500',
      icon: 'text-purple-600',
      border: 'border-purple-200',
      light: 'bg-purple-50'
    },
    'text-teal-500': {
      bg: 'bg-teal-500',
      icon: 'text-teal-600',
      border: 'border-teal-200',
      light: 'bg-teal-50'
    },
    'text-cyan-500': {
      bg: 'bg-cyan-500',
      icon: 'text-cyan-600',
      border: 'border-cyan-200',
      light: 'bg-cyan-50'
    }
  };
  const colors = colorMap[module.color] || {
    bg: 'bg-slate-500',
    icon: 'text-slate-600',
    border: 'border-slate-200',
    light: 'bg-slate-50'
  };
  return <motion.button initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.05
  }} onClick={onClick} className={`
        relative p-4 rounded-2xl border-2 text-left transition-all group
        ${isMastered ? 'border-emerald-300 bg-emerald-50' : `${colors.border} ${colors.light}`}
        hover:shadow-lg hover:-translate-y-1
      `} data-id="element-3605">
      {/* Mastered star */}
      {isMastered && <div className="absolute -top-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center shadow-md" data-id="element-3606">
          <span className="text-white text-sm" data-id="element-3607">⭐</span>
        </div>}

      {/* Icon */}
      <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center mb-3 shadow-sm group-hover:scale-105 transition-transform`} data-id="element-3608">
        <IconComponent className="w-6 h-6 text-white" strokeWidth={2.5} data-id="element-3609" />
      </div>

      {/* Title */}
      <h3 className="font-bold text-slate-900 text-sm leading-tight line-clamp-2 mb-2" data-id="element-3610">
        {title}
      </h3>

      {/* Simple status indicator */}
      <div className="flex items-center gap-1.5" data-id="element-3611">
        {isMastered ? <span className="text-xs font-semibold text-emerald-600" data-id="element-3612">Klaar!</span> : isStarted ? <>
            <div className="flex gap-0.5" data-id="element-3613">
              {[0, 1, 2, 3].map(i => <div key={i} className={`w-1.5 h-1.5 rounded-full ${i < Math.ceil(mastery / 25) ? colors.bg : 'bg-slate-200'}`} data-id="element-3614" />)}
            </div>
            <span className="text-xs font-medium text-slate-500" data-id="element-3615">Bezig</span>
          </> : <span className="text-xs font-medium text-slate-400" data-id="element-3616">Nieuw</span>}
      </div>
    </motion.button>;
}